/**
 * WHAT THE PRINTER IS DOING, IN THE WORDS OF THE PERSON STANDING AT IT.
 *
 * The printer speaks in status bytes (`1e 52 16 13 10`). The person at a help desk — a fan in front of
 * them, a queue behind — needs one sentence and one thing to do. Every surface (the top-bar chip, the
 * printer panel, the print sheet) renders THIS, so they can never disagree about whether it is safe
 * to print.
 *
 * Ordered by what blocks the person, not by what the code checks first. Each cause was met on the
 * real EP802 (serial MS532I0, firmware APP22-11-11-A5) — see ~/printer/KNOWLEDGE.md.
 */

import { missingEssentials, CHECK_NAMES, type PrinterProfile } from '../masung-printer/profile';

export type Tone = 'red' | 'amber' | 'green' | 'blue' | 'grey';

export interface PrinterSituation {
  /** Short, for the chip. */
  short: string;
  /** The sentence. */
  headline: string;
  /** What to physically do. Absent when there is nothing to do. */
  todo?: string;
  tone: Tone;
  /** Sending a job now would be lost, held, or wasted — do not. */
  blocksPrinting: boolean;
  /** Which button the panel should lead with. */
  action?: 'connect' | 'setup' | 'recheck' | 'verify' | 'none';
  /** Raw evidence, for the diagnostics view and the support report. */
  evidence?: string;
}

export interface PrinterFacts {
  supported: boolean;
  secure: boolean;
  connected: boolean;
  connecting: boolean;
  /** A printer was granted to this site before — so "not connected" means unplugged, not never set up. */
  knownPrinter: boolean;
  connectError?: string | null;
  restarting: boolean;
  /** DLE EOT 1..5; null entries = no reply. Null = not read yet. */
  status: (number | null)[] | null;
  profile: PrinterProfile | null;
  printing?: { index: number; total: number } | null;
}

const hex = (b: number | null) => (b === null ? '--' : b.toString(16).padStart(2, '0'));

export function describe(f: PrinterFacts): PrinterSituation {
  if (!f.supported) {
    return { short: 'No USB', tone: 'grey', blocksPrinting: true, action: 'none',
      headline: "This browser can't reach a USB printer",
      todo: 'Open the back office in Google Chrome or Microsoft Edge on the computer the printer is plugged into.' };
  }
  if (!f.secure) {
    return { short: 'Not secure', tone: 'grey', blocksPrinting: true, action: 'none',
      headline: 'The printer can only be used over https',
      todo: 'Open the back office on its https:// address.' };
  }
  if (f.printing) {
    return { short: `Printing ${f.printing.index}/${f.printing.total}`, tone: 'blue', blocksPrinting: true, action: 'none',
      headline: `Printing ticket ${f.printing.index} of ${f.printing.total}`,
      todo: 'Leave the lid closed and let the tickets come out.' };
  }
  if (f.restarting) {
    return { short: 'Restarting', tone: 'blue', blocksPrinting: true, action: 'none',
      headline: 'The printer is restarting to save a setting',
      todo: 'It comes back by itself in a few seconds.' };
  }
  if (f.connecting) {
    return { short: 'Connecting', tone: 'blue', blocksPrinting: true, action: 'none', headline: 'Connecting to the printer…' };
  }
  if (!f.connected) {
    if (f.connectError) {
      // The two that actually happen at a desk: another tab has it (the box office till open in the
      // next tab on the same laptop), or the OS has it installed as a normal printer.
      const busy = /another|holding|claim|Access denied|SecurityError/i.test(f.connectError);
      return { short: busy ? 'Printer busy' : 'Not connected', tone: 'red', blocksPrinting: true, action: 'connect',
        headline: busy ? 'Another tab or program is using the printer' : 'Could not connect to the printer',
        todo: busy
          ? 'Close the other tab using it (often the box office till), then press Connect. A USB printer can only talk to one tab at a time.'
          : f.connectError,
        evidence: f.connectError };
    }
    return f.knownPrinter
      ? { short: 'Printer off', tone: 'amber', blocksPrinting: true, action: 'connect',
          headline: 'The printer is unplugged or switched off',
          todo: 'Check its power switch and USB cable. It reconnects by itself when it comes back.' }
      : { short: 'No printer', tone: 'grey', blocksPrinting: true, action: 'connect',
          headline: 'No ticket printer connected',
          todo: 'Plug the ticket printer into this computer by USB, switch it on, then press Connect and pick it from the list.' };
  }

  // ── Connected. What does the printer itself say? ──
  const s = f.status;
  const ev = s ? `status ${s.map(hex).join(' ')}` : 'status not read yet';
  if (s && s.every((b) => b === null)) {
    return withSetup(f, { short: 'Not answering', tone: 'amber', blocksPrinting: false, action: 'recheck',
      headline: "The printer is connected but isn't answering",
      todo: "Tickets may still print, but problems (paper out, lid open) can't be seen. If nothing prints, unplug the USB cable and plug it back in.",
      evidence: ev });
  }
  if (s && s[0] !== null) {
    const [s1, s2, s3, s4, s5] = s;
    const offline = !!(s1! & 0x08);
    // Order: the cause a person can see and fix first.
    if (s2 !== null && s2 & 0x04) return held(offline, { short: 'Lid open', headline: 'The printer lid is open', todo: 'Close it firmly until it clicks.', evidence: ev });
    if ((s4 !== null && (s4 & 0x60) === 0x60) || (s2 !== null && s2 & 0x20)) {
      const how = f.profile?.paperType === 'gap' ? ' with the notch line over the sensor, as it was set up'
        : f.profile?.paperType === 'mark' ? ' with the black marks on the side facing the sensor' : '';
      return held(offline, { short: 'Out of paper', headline: 'The printer is out of paper',
        todo: `Load a new roll${how}, close the lid, and press Check again.`, evidence: ev });
    }
    if (s3 !== null && s3 & 0x08) return held(offline, { short: 'Cutter jam', headline: 'The cutter is stuck', todo: 'Open the lid, remove any paper caught at the blade, close it.', evidence: ev });
    if (s5 !== null && s5 & 0x08) return held(offline, { short: 'Paper jam', headline: 'Paper is jammed inside', todo: 'Open the lid, pull the jammed paper out, tear it straight, reload and close.', evidence: ev });
    if (s5 !== null && s5 & 0x01) return held(offline, { short: 'Exit blocked', headline: 'Paper is blocked at the exit', todo: 'Clear the paper exit slot.', evidence: ev });
    if (offline && s3 !== null && s3 & 0x04) {
      // ✅ FIELD: the EP802's state after a mark search fails — 1e 52 16 13. Only the lid clears it.
      return held(true, { short: 'Notch not found', headline: "The printer couldn't find the ticket notch",
        todo: 'Open and close the lid to clear it. If it happens again, the roll is loaded crooked or the sensor is not under the notch line — check both before printing again.',
        evidence: ev });
    }
    if (s3 !== null && s3 & 0x40) return held(offline, { short: 'Too hot', headline: 'The print head is too hot', todo: 'Wait a minute — it recovers by itself.', evidence: ev });
    if (offline) return held(true, { short: 'Printer error', headline: 'The printer reports an error', todo: 'Open and close the lid. If it stays, switch the printer off and on.', evidence: ev });

    const notes: { short: string; text: string }[] = [];
    if (s4 !== null && (s4 & 0x0c) === 0x0c) notes.push({ short: 'Paper low', text: 'Paper is running low — have a roll ready.' });
    if (s5 !== null && s5 & 0x04) notes.push({ short: 'Take ticket', text: 'A ticket is waiting at the exit — take it.' });
    const sit = withSetup(f, { short: 'Ready', tone: 'green', blocksPrinting: false, action: 'none', headline: 'Ready to print', evidence: ev });
    if (notes.length && sit.tone === 'green') return { ...sit, tone: 'amber', short: notes[0].short, todo: notes.map((n) => n.text).join(' ') };
    if (notes.length) return { ...sit, todo: [sit.todo, ...notes.map((n) => n.text)].filter(Boolean).join(' ') };
    return sit;
  }
  return withSetup(f, { short: 'Connected', tone: 'green', blocksPrinting: false, action: 'none', headline: 'Connected', evidence: ev });
}

/**
 * An error state is not just "can't print now". ✅ FIELD: this printer HOLDS a job sent while it is
 * in error and prints it the moment the lid is closed — tickets appearing by surprise minutes later.
 */
function held(offline: boolean, s: { short: string; headline: string; todo: string; evidence: string }): PrinterSituation {
  return { ...s, tone: 'red', blocksPrinting: true, action: 'recheck',
    todo: offline ? `${s.todo} Nothing will be sent until it's fixed — the printer would hold the job and print it later by surprise.` : s.todo };
}

/** A working printer that was never measured for this stock prints in the wrong place — say so. */
function withSetup(f: PrinterFacts, s: PrinterSituation): PrinterSituation {
  const p = f.profile;
  if (!p || !p.paperType) {
    return { ...s, short: s.tone === 'green' ? 'Not set up' : s.short, tone: s.tone === 'green' ? 'amber' : s.tone, action: 'setup',
      headline: s.tone === 'green' ? "Connected, but not set up for this ticket stock" : s.headline,
      todo: [s.tone === 'green' ? undefined : s.todo, 'Run setup once (about 10 minutes, a few tickets of paper). Until then tickets can print in the wrong place or not cut on the perforation.'].filter(Boolean).join(' ') };
  }
  const missing = missingEssentials(p);
  // Only the proof is missing — and it is a question about paper already in the operator's hand,
  // not a reason to send them back through a ten-step wizard.
  if (missing.length === 1 && missing[0] === 'verify' && s.tone === 'green') {
    return { ...s, tone: 'amber', short: 'Confirm a ticket', action: 'verify',
      todo: 'Look at the last ticket printed: cut on the perforation, text clear of the printed border and artwork, QR opens on a phone camera. If all three are right, confirm it — that finishes setup.' };
  }
  if (missing.length && s.tone === 'green') {
    return { ...s, tone: 'amber', short: 'Setup unfinished', action: 'setup',
      todo: `Setup still needs: ${missing.map((k) => CHECK_NAMES[k] ?? k).join(', ')}. It will print, but that part is unproven.` };
  }
  return s;
}
